import { type ReactNode, startTransition, useState } from 'react';
import { Autocomplete } from '@gonfalon/launchpad-experimental/Autocomplete';
import {
  Button,
  DialogTrigger,
  Group,
  IconButton,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
  Tag,
  TagGroup as RACTagGroup,
  TagList,
  Text,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { useFieldContext } from '../../contexts';

import { FormFieldError } from './FormFieldError';
import {
  type BrowserExtensionControlProps,
  getDisableBrowserExtensionAttributes,
} from './getDisableBrowserExtensionAttributes';

import styles from './TagGroup.module.css';

export interface TagGroupProps extends BrowserExtensionControlProps {
  label?: string;
  description?: ReactNode;
  placeholder?: string;
}

interface TagItem {
  id: string;
  name: string;
  isNew?: boolean;
}

export function TagGroup({
  label = 'Tags',
  description,
  placeholder = 'Search or enter new tags...',
  disableBrowserExtensions = true,
}: TagGroupProps) {
  const field = useFieldContext<string[]>();
  const [filterText, setFilterText] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const tags = field.state.value || [];
  const selectedKeys = new Set(tags);

  const handleSelectionChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') {
      return; // Don't allow selecting all
    }

    const newTags = Array.from(keys).map((key) => String(key));
    field.handleChange(newTags);
  };

  // Create items based on current tags and filter text
  const existingItems: TagItem[] = tags.map((tag) => ({ id: tag, name: tag }));
  let items: TagItem[] = existingItems;

  // If there's filter text and it's not already a tag, offer to create it
  if (filterText.trim() && !tags.includes(filterText.trim())) {
    items = [
      { id: filterText.trim(), name: filterText.trim(), isNew: true },
      ...existingItems.filter((item) => item.name.toLowerCase().includes(filterText.toLowerCase())),
    ];
  }

  const emptySelection = selectedKeys.size === 0;
  const tagCount = tags.length;

  return (
    <div className={styles.tagGroup}>
      <div className={styles.tagGroupHeader}>
        <Label>{label}</Label>
        <DialogTrigger isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <IconButton icon="add" size="small" variant="minimal" aria-label="Add tag" />

          <Popover placement="bottom left">
            <Autocomplete
              defaultInputValue={filterText}
              onInputChange={(nextFilter) => startTransition(() => setFilterText(nextFilter))}
            >
              <SearchField aria-label={`Search ${label}…`} autoFocus>
                <Group>
                  <Icon name="search" size="small" />
                  <Input
                    placeholder={placeholder}
                    {...getDisableBrowserExtensionAttributes(disableBrowserExtensions)}
                  />
                  <IconButton aria-label="clear" icon="cancel-circle-outline" size="small" variant="minimal" />
                </Group>
              </SearchField>

              <ListBox
                className={styles.listbox}
                selectionMode="multiple"
                items={items}
                selectedKeys={selectedKeys}
                onSelectionChange={handleSelectionChange}
                renderEmptyState={() => (
                  <span className={styles.emptyState}>
                    {filterText.trim() ? `No matching ${label}` : `Start typing to add ${label}`}
                  </span>
                )}
              >
                {(item) => (
                  <ListBoxItem id={item.id}>
                    {item.isNew ? (
                      <span className={styles.new}>
                        Add: <i>{item.name}</i>
                      </span>
                    ) : (
                      item.name
                    )}
                  </ListBoxItem>
                )}
              </ListBox>

              {!emptySelection && (
                <div className={styles.clearContainer}>
                  <Button variant="minimal" size="small" onPress={() => handleSelectionChange(new Set())}>
                    Clear all
                  </Button>
                </div>
              )}
            </Autocomplete>
          </Popover>
        </DialogTrigger>
      </div>

      {tagCount > 0 && (
        <RACTagGroup
          aria-label={`Selected ${label}`}
          selectionMode="multiple"
          onRemove={(keys: 'all' | Set<React.Key>) => {
            if (keys === 'all') {
              field.handleChange([]);
            } else {
              const keysToRemove = Array.from(keys);
              const newTags = tags.filter((tag) => !keysToRemove.includes(tag));
              field.handleChange(newTags);
            }
          }}
        >
          <TagList>
            {tags.map((tag) => (
              <Tag key={tag} id={tag} textValue={tag}>
                {tag}
              </Tag>
            ))}
          </TagList>
        </RACTagGroup>
      )}

      {description && <Text slot="description">{description}</Text>}
      <FormFieldError meta={field.state.meta} />
    </div>
  );
}
